// PublicVerify.js — v1.9.8
// Public receipt verification page. Reachable at /verify/:token without login.
// Members hand-hold the printed CR under a QR reader; the QR encodes
// https://<host>/verify/<token> and the scanner opens this page in a browser.
// We hit /api/verify/:token (also public) and render minimum data required to
// confirm the CR is legitimate. No sensitive info.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiAlertTriangle } from 'react-icons/fi';
import axios from 'axios';

const fmt = (v, sym) => (parseFloat(v || 0) > 0
  ? `${sym}${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
  : null);

const PublicVerify = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    axios.get(`/api/verify/${encodeURIComponent(token)}`)
      .then(r => setState({ loading: false, data: r.data, error: null }))
      .catch(e => setState({ loading: false, data: null, error: e.response?.data?.error || 'Not found' }));
  }, [token]);

  const card = { maxWidth: 520, margin: '48px auto', background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.08)', padding: 32, textAlign: 'center', border: '1px solid #e5e7eb' };
  const wrap = { minHeight: '100vh', background: '#f5f5f7', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 16 };

  if (state.loading) {
    return <div style={wrap}><div style={card}><div style={{ color: '#6b7280' }}>Verifying…</div></div></div>;
  }
  if (state.error) {
    return (
      <div style={wrap}>
        <div style={card}>
          <FiXCircle style={{ fontSize: 60, color: '#dc2626' }} />
          <h1 style={{ margin: '14px 0 4px', fontSize: 22 }}>Not verified</h1>
          <p style={{ color: '#6b7280', marginTop: 0 }}>
            This receipt code doesn&#39;t match any record in the church system.
            If you scanned a QR from a printed receipt, it may have been altered
            or the receipt may not be genuine.
          </p>
        </div>
      </div>
    );
  }
  const d = state.data;
  const amtParts = [
    fmt(d.amount_usd || d.amounts?.$, '$'),
    fmt(d.amount_kwacha || d.amounts?.K, 'K'),
  ].filter(Boolean);
  const isReversed = d.reversed;

  return (
    <div style={wrap}>
      <div style={card}>
        {isReversed ? (
          <>
            <FiAlertTriangle style={{ fontSize: 60, color: '#f59e0b' }} />
            <h1 style={{ margin: '14px 0 4px', fontSize: 22, color: '#92400e' }}>Reversed receipt</h1>
            <p style={{ color: '#6b7280', marginTop: 0 }}>
              This receipt was issued but has since been reversed by the treasurer.
            </p>
          </>
        ) : (
          <>
            <FiCheckCircle style={{ fontSize: 60, color: '#16a34a' }} />
            <h1 style={{ margin: '14px 0 4px', fontSize: 22, color: '#166534' }}>Verified genuine</h1>
            <p style={{ color: '#6b7280', marginTop: 0 }}>Receipt confirmed by {d.church_name}.</p>
          </>
        )}
        <div style={{ marginTop: 24, textAlign: 'left', borderTop: '1px solid #e5e7eb', paddingTop: 18 }}>
          <Row label="Receipt no." value={d.cr_number} />
          <Row label="Date" value={d.date} />
          <Row label="Received from" value={d.received_from} />
          <Row label="Fund" value={d.fund_name} />
          <Row label="Amount" value={amtParts.length ? amtParts.join(' + ') : '—'} strong />
          <Row label="Method" value={String(d.payment_method || '').replace(/-/g, ' ')} />
        </div>
        <div style={{ marginTop: 22, fontSize: 11, color: '#9ca3af' }}>
          This page shows the minimum information needed to confirm the receipt.
          No personal contact details are disclosed.
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', padding: '6px 0', fontSize: 13, borderBottom: '1px dotted #e5e7eb' }}>
    <div style={{ width: 130, color: '#6b7280' }}>{label}</div>
    <div style={{ flex: 1, fontWeight: strong ? 800 : 600, color: '#111827', textTransform: label === 'Method' ? 'capitalize' : 'none' }}>{value || '—'}</div>
  </div>
);

export default PublicVerify;
